import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, SecondaryButton } from '../styles';
import SequenceStep from './SequenceStep';
import AddStepButton from './AddStepButton';

// Local storage key for storing email sequences
const SEQUENCE_STORAGE_KEY = 'email_sequences';
const TEMPLATES_STORAGE_KEY = 'email_templates';

const EmailSequenceBuilder = () => {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [currentSequence, setCurrentSequence] = useState(null);
  const [sequenceName, setSequenceName] = useState('');
  const [steps, setSteps] = useState([{ id: 1, templateId: '', delay: 0, isInitial: true }]);
  const [isEditing, setIsEditing] = useState(false);
  const [showSequenceForm, setShowSequenceForm] = useState(false);
  const [errors, setErrors] = useState({});

  // Load sequences and templates from local storage
  useEffect(() => {
    const savedSequences = localStorage.getItem(SEQUENCE_STORAGE_KEY);
    const savedTemplates = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    
    if (savedSequences) {
      setSequences(JSON.parse(savedSequences));
    }
    
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    }
  }, []);

  // Save sequences to local storage
  useEffect(() => {
    localStorage.setItem(SEQUENCE_STORAGE_KEY, JSON.stringify(sequences));
  }, [sequences]);

  // Handle creating a new sequence
  const handleCreateSequence = () => {
    setCurrentSequence(null);
    setSequenceName('');
    setSteps([{ id: 1, templateId: '', delay: 0, isInitial: true }]);
    setIsEditing(false);
    setShowSequenceForm(true);
    setErrors({});
  };

  // Handle editing an existing sequence
  const handleEditSequence = (sequence) => {
    setCurrentSequence(sequence);
    setSequenceName(sequence.name);
    setSteps(sequence.steps);
    setIsEditing(true);
    setShowSequenceForm(true);
    setErrors({});
  };

  // Handle deleting a sequence
  const handleDeleteSequence = (id) => {
    if (window.confirm('Are you sure you want to delete this sequence?')) {
      setSequences(sequences.filter(seq => seq.id !== id));
      
      if (currentSequence && currentSequence.id === id) {
        handleCancelEdit();
      }
    }
  };

  // Add a new step to the sequence
  const handleAddStep = (afterStepId) => {
    // Find the highest existing ID to ensure uniqueness
    const maxId = steps.reduce((max, step) => Math.max(max, step.id), 0);
    
    // Find the index of the step after which to add the new step
    const stepIndex = steps.findIndex(step => step.id === afterStepId);
    
    if (stepIndex === -1) return;
    
    // Create the new step
    const newStep = {
      id: maxId + 1,
      templateId: '',
      delay: 1, // Default to 1 day
      isInitial: false,
    };
    
    // Insert the new step after the specified step
    const updatedSteps = [
      ...steps.slice(0, stepIndex + 1),
      newStep,
      ...steps.slice(stepIndex + 1)
    ];
    
    setSteps(updatedSteps);
  };

  // Update a step in the sequence
  const handleUpdateStep = (updatedStep) => {
    setSteps(steps.map(step => 
      step.id === updatedStep.id ? updatedStep : step
    ));
  };

  // Remove a step from the sequence
  const handleRemoveStep = (stepId) => {
    // Don't allow removing the initial step
    if (steps.find(step => step.id === stepId)?.isInitial) {
      return;
    }
    
    setSteps(steps.filter(step => step.id !== stepId));
  };

  // Handle canceling the edit/create operation
  const handleCancelEdit = () => {
    setShowSequenceForm(false);
    setCurrentSequence(null);
    setSequenceName('');
    setSteps([{ id: 1, templateId: '', delay: 0, isInitial: true }]);
    setIsEditing(false);
    setErrors({});
  };

  // Handle saving the sequence
  const handleSaveSequence = () => {
    // Validate sequence
    const newErrors = {};
    
    if (!sequenceName.trim()) {
      newErrors.name = 'Sequence name is required';
    }
    
    // Check if each step has a template selected
    const invalidSteps = steps.filter(step => !step.templateId);
    if (invalidSteps.length > 0) {
      newErrors.steps = 'All steps must have a template selected';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Clear errors
    setErrors({});
    
    // Prepare the sequence data
    const sequenceData = {
      id: isEditing && currentSequence ? currentSequence.id : Date.now().toString(),
      name: sequenceName,
      steps: steps,
      createdAt: isEditing && currentSequence ? currentSequence.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    if (isEditing && currentSequence) {
      // Update existing sequence
      setSequences(sequences.map(seq => 
        seq.id === currentSequence.id ? sequenceData : seq
      ));
    } else {
      // Add new sequence
      setSequences([...sequences, sequenceData]);
    }
    
    // Reset state
    setShowSequenceForm(false);
    setCurrentSequence(null);
    setSequenceName('');
    setSteps([{ id: 1, templateId: '', delay: 0, isInitial: true }]);
    setIsEditing(false);
  };

  // Render the sequence builder or list of sequences
  return (
    <Container>
      <h1>Email Sequence Builder</h1>
      
      {!showSequenceForm ? (
        <SequenceListContainer>
          <SequenceHeaderContainer>
            <h2>Your Sequences</h2>
            <Button onClick={handleCreateSequence}>Create New Sequence</Button>
          </SequenceHeaderContainer>
          
          {sequences.length === 0 ? (
            <EmptyState>
              <p>No sequences created yet. Create your first sequence!</p>
              <Button onClick={handleCreateSequence}>Create Sequence</Button>
            </EmptyState>
          ) : (
            <SequencesList>
              {sequences.map(sequence => (
                <SequenceCard key={sequence.id}>
                  <SequenceCardHeader>
                    <SequenceName>{sequence.name}</SequenceName>
                    <SequenceActions>
                      <ActionButton onClick={() => handleEditSequence(sequence)}>
                        Edit
                      </ActionButton>
                      <ActionButton onClick={() => handleDeleteSequence(sequence.id)}>
                        Delete
                      </ActionButton>
                    </SequenceActions>
                  </SequenceCardHeader>
                  
                  <SequenceStepCount>
                    {sequence.steps.length} {sequence.steps.length === 1 ? 'email' : 'emails'} in sequence
                  </SequenceStepCount>
                  
                  <SequenceMetadata>
                    <div>Created: {new Date(sequence.createdAt).toLocaleDateString()}</div>
                    <div>Updated: {new Date(sequence.updatedAt).toLocaleDateString()}</div>
                  </SequenceMetadata>
                </SequenceCard>
              ))}
            </SequencesList>
          )}
        </SequenceListContainer>
      ) : (
        <SequenceBuilderContainer>
          <h2>{isEditing ? 'Edit Sequence' : 'Create New Sequence'}</h2>
          
          <SequenceNameContainer>
            <label htmlFor="sequenceName">Sequence Name</label>
            <SequenceNameInput
              type="text"
              id="sequenceName"
              value={sequenceName}
              onChange={(e) => setSequenceName(e.target.value)}
              hasError={!!errors.name}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </SequenceNameContainer>
          
          {errors.steps && <ErrorMessage>{errors.steps}</ErrorMessage>}
          
          <SequenceStepsContainer>
            {steps.map((step, index) => (
              <StepContainer key={step.id}>
                <SequenceStep
                  step={step}
                  templates={templates}
                  onUpdate={handleUpdateStep}
                  onRemove={handleRemoveStep}
                  isRemovable={!step.isInitial && steps.length > 1}
                />
                
                {index < 9 && (
                  <AddStepButton 
                    onAdd={() => handleAddStep(step.id)} 
                    disabled={steps.length >= 10}
                  />
                )}
              </StepContainer>
            ))}
          </SequenceStepsContainer>
          
          <ButtonContainer>
            <Button onClick={handleSaveSequence}>
              {isEditing ? 'Update Sequence' : 'Save Sequence'}
            </Button>
            <SecondaryButton onClick={handleCancelEdit}>Cancel</SecondaryButton>
          </ButtonContainer>
        </SequenceBuilderContainer>
      )}
    </Container>
  );
};

export default EmailSequenceBuilder;

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  h1 {
    margin-bottom: 20px;
    color: #333;
  }
`;

const SequenceListContainer = styled.div`
  width: 100%;
`;

const SequenceHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    color: #333;
  }
`;

const EmptyState = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  color: #666;
  border: 1px dashed #ccc;
  
  button {
    margin-top: 16px;
  }
`;

const SequencesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const SequenceCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #eee;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const SequenceCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SequenceName = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #333;
`;

const SequenceActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  background-color: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f5f5f5;
    border-color: #999;
  }
`;

const SequenceStepCount = styled.div`
  margin: 8px 0;
  font-size: 14px;
  color: #666;
`;

const SequenceMetadata = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #888;
  margin-top: 12px;
  border-top: 1px solid #eee;
  padding-top: 12px;
`;

const SequenceBuilderContainer = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  h2 {
    margin-bottom: 20px;
    color: #333;
  }
`;

const SequenceNameContainer = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    color: #444;
  }
`;

const SequenceNameInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.hasError ? '#f44336' : '#ccc'};
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#f44336' : '#2196f3'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'rgba(244, 67, 54, 0.2)' : 'rgba(33, 150, 243, 0.2)'};
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  font-size: 12px;
  margin-top: 5px;
`;

const SequenceStepsContainer = styled.div`
  margin-top: 30px;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 30px;
`; 